import React, { useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import Seo from "../Components/Seo";
import SectionHeading from "../Components/SectionHeading";
import CTASection from "../Components/CTASection";
import { getIcon } from "../lib/icons";
import { getServiceBySlug } from "../data/services";
import { SITE } from "../data/site";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
};

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return <Navigate to="/404" replace />;
  }

  const Icon = getIcon(service.icon);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    serviceType: service.name,
    description: service.seoDescription,
    url: `${SITE.domain}/services/${service.slug}`,
    areaServed: "United States",
    provider: {
      "@type": "Organization",
      name: SITE.name,
      url: SITE.domain,
    },
  };

  return (
    <>
      <Seo
        title={service.seoTitle}
        description={service.seoDescription}
        path={`/services/${service.slug}`}
        image={service.image}
        jsonLd={jsonLd}
      />

      {/* Hero (dark) */}
      <section className="relative overflow-hidden bg-brand-gradient-dark">
        <div className="pointer-events-none absolute -left-24 top-0 h-96 w-96 rounded-full bg-primary/30 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-24 h-96 w-96 rounded-full bg-accent/20 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_55%)]" />
        <div className="relative mx-auto max-w-7xl px-4 pb-20 pt-32 sm:px-6 lg:px-8 lg:pb-24 lg:pt-36">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-amber-300 ring-1 ring-white/20 backdrop-blur">
                <Icon className="h-7 w-7" />
              </span>
              <h1 className="mt-6 font-display text-4xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
                {service.hero.headline}
              </h1>
              <p className="mt-5 max-w-xl text-lg text-purple-100/80">
                {service.hero.subheadline}
              </p>
              <motion.button
                whileHover={{ scale: 1.04 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => navigate("/contact")}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-gradient px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/40"
              >
                Schedule Consultation
                <ArrowRight className="h-5 w-5" />
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.15 }}
              className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40"
            >
              <img
                src={service.image}
                alt={service.name}
                className="h-full w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="px-4 py-16 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-lg leading-relaxed text-muted-foreground">{service.overview}</p>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-card/40 px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="Benefits" title="Why It Matters" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {service.benefits.map((b, i) => (
              <motion.div
                key={b.title}
                {...fadeUp}
                transition={{ duration: 0.5, delay: (i % 4) * 0.08 }}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <h3 className="font-display text-lg font-semibold text-foreground">{b.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{b.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="Use Cases" title="What We Build" />
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {service.useCases.map((useCase, i) => (
              <motion.div
                key={useCase}
                {...fadeUp}
                transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                className="flex items-start gap-3 rounded-2xl border border-border bg-card p-5"
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
                  <Check className="h-4 w-4" />
                </span>
                <span className="text-sm text-foreground/90">{useCase}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="bg-card/40 px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <SectionHeading eyebrow="Technologies" title="Our Toolkit" />
          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {service.technologies.map((tech) => (
              <span
                key={tech}
                className="rounded-xl border border-border bg-background px-4 py-2 text-sm font-medium text-foreground/80"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="Process" title="How We Deliver" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {service.process.map((step, i) => (
              <motion.div
                key={step}
                {...fadeUp}
                transition={{ duration: 0.45, delay: (i % 4) * 0.08 }}
                className="rounded-2xl border border-border bg-card p-6"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-gradient font-display text-sm font-bold text-white">
                  {i + 1}
                </span>
                <h3 className="mt-4 font-display text-base font-semibold text-foreground">
                  {step}
                </h3>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        title={`Ready to start your ${service.name.toLowerCase()} project?`}
        text="Let's discuss your goals and map the fastest path to a production-ready solution."
      />
    </>
  );
};

export default ServiceDetailPage;
